/**
 * `/studio/clients` — `mockups/studio/clients.html`. The book.
 *
 * One read: `clients_summary` with the seg's own status. Everything on the page is a
 * field of `StudioClientsListSchema` or a subtotal of the rows directly below it, and
 * the one thing worth reading the file for is **why the hero does not move when the
 * seg does**.
 *
 * `readmodels/studio-clients-list.ts` computes `concentration` over every *live*
 * client **before** the status filter is applied (`.docs/reports/17-report.md`: "a
 * studio tab narrowed to 'Active' must not silently drop a past-due or paused client
 * out of its own denominator"). So the answer sentence and the hero figures are the
 * whole book, in every tab; the section head's "N under-scoped · M past due" and the
 * states fold are subtotals of what is on screen and say so. Mixing the two would put
 * a share of a denominator nobody can see next to a count of rows they can.
 *
 * The wizard is a route, not a dialog: `.docs/20` §B's wizard is a sequence of
 * actions that have each already committed, so it has to survive a reload and be a
 * link. "New client" therefore navigates rather than opening an `ActionDialog`.
 */

import { StudioClientsListSchema, StudioClientsListStatusSchema } from '@fareast/shared'
import { Link } from '@tanstack/react-router'
import { z } from 'zod'

import { useStudioRead } from '../../../api/studio.js'
import { Button, Seg, SegItem } from '../../../components/portal/Button.js'
import { Card, CardBody, CardMore } from '../../../components/portal/Card.js'
import { Empty, Fold } from '../../../components/portal/Disclosure.js'
import { Section, SectionHead } from '../../../components/portal/Layout.js'
import { Region, RegionCols } from '../../../components/portal/Region.js'
import {
  Answer,
  B,
  HeroFigure,
  More,
  Muted,
  Prose,
  Small,
  SubFigure,
} from '../../../components/portal/Text.js'
import { HeroCapInfo } from '../../../components/studio/HeroCapInfo.js'
import { useFormat } from '../../../format/useFormat.js'
import { useSearchState } from '../../../search/useSearchState.js'
import { PageTitle } from '../../../shell/PageTitle.js'
import { ClientsBook, bookCounts } from './ClientsBook.js'
import {
  DECISIONS_ANCHOR,
  DecisionsSection,
  clientsWaiting,
  ownedByStudio,
} from './DecisionsSection.js'
import { SHARE_DIGITS, percentFigure, percentText } from '../../../format/percent.js'

/**
 * The mockup's own four and "No retainer", which is `clients_summary`'s own input. One
 * list, not two.
 */
export const ClientsSearchSchema = z.object({
  status: StudioClientsListStatusSchema.default('active'),
})

const STATUS_LABELS = {
  active: 'Active',
  past_due: 'Past due',
  cancelled: 'Cancelled',
  no_retainer: 'No retainer',
  all: 'All',
} as const

/**
 * What a seg lists, as the noun a sentence needs — "No client is no retainer." is not
 * one, and neither was the "No client is all." the empty state used to print.
 */
const STATUS_CLIENTS = {
  active: 'active clients',
  past_due: 'past due clients',
  cancelled: 'cancelled clients',
  no_retainer: 'clients with no retainer',
  all: 'clients',
} as const

/** The empty table, per seg. */
const STATUS_EMPTY = {
  active: 'No client is active.',
  past_due: 'No client is past due.',
  cancelled: 'No client is cancelled.',
  no_retainer: 'Every client has a retainer.',
  all: 'There is no client yet.',
} as const

/** Whole units where there are no cents — see `ClientsBook.tsx`'s own note. */
const MONEY = { showCents: 'auto' } as const

export function ClientsPage() {
  const [search, setSearch] = useSearchState(ClientsSearchSchema)
  const format = useFormat()
  const book = useStudioRead('clients_summary', { status: search.status })

  const title = (
    <PageTitle
      actions={
        <>
          <Seg label="Retainer status">
            {Object.entries(STATUS_LABELS).map(([value, label]) => (
              <SegItem
                key={value}
                on={search.status === value}
                onClick={() => {
                  setSearch({ status: value as typeof search.status })
                }}
              >
                {label}
              </SegItem>
            ))}
          </Seg>
          <Button asChild variant="primary">
            <Link to="/studio/clients/new">New client</Link>
          </Button>
        </>
      }
      eyebrow="Studio"
      title="Clients"
    />
  )

  if (book.data === undefined) {
    return (
      <>
        {title}
        <Empty>Reading the book…</Empty>
      </>
    )
  }

  /*
   * Parsed rather than cast.
   *
   * `useStudioRead` types the answer from the RPC client, which is the handler's
   * return type and not the schema's — so a field the read model renamed would be a
   * `undefined` on screen rather than a failure. The schema is the one both ends
   * agreed on; parsing it here is the same guard `test/world/index.ts` `studioBody`
   * applies to the fixture.
   */
  const view = StudioClientsListSchema.parse(book.data)
  const { rows, concentration, decisions } = view
  const counts = bookCounts(rows)
  const waiting = clientsWaiting(decisions)
  const ours = ownedByStudio(decisions)
  const filterLabel = STATUS_LABELS[search.status]

  return (
    <>
      {title}

      <div className="studio-hero studio-hero--n5">
        <Region>
          <Answer>
            Every live retainer together is <B>{format.money(concentration.total_mrr, MONEY)}</B> a
            month. Losing the largest{' '}
            {format.qty(concentration.clients_to_lose_half, { one: 'client', other: 'clients' })}{' '}
            would take half of that with them.
          </Answer>

          <RegionCols>
            <div>
              <HeroCapInfo
                label="Top two share"
                tip="How much of monthly recurring revenue sits with the two biggest accounts. Two of those going badly in the same month would halve revenue. Measured over every live client — it does not move when the tab above does."
              />
              <HeroFigure
                sup="%"
                value={percentFigure(format, concentration.top2_share, SHARE_DIGITS)}
              />
            </div>

            <div>
              <HeroCapInfo
                label="Waiting"
                tip="How many clients have at least one open proposal, chase or overdue quote. The number is clients, not decisions — one client can wait on several."
              />
              <SubFigure value={waiting} />
              <Small>
                <Muted>
                  {decisions.length === 0
                    ? 'Nothing open.'
                    : `${format.number(decisions.length)} decisions${
                        ours === 0 ? '' : `, ${format.number(ours)} ours`
                      }.`}{' '}
                  <More href={`#${DECISIONS_ANCHOR}`}>List →</More>
                </Muted>
              </Small>
            </div>

            <div>
              <HeroCapInfo
                label="Top client"
                tip="The single largest live retainer as a share of all monthly recurring revenue."
              />
              <SubFigure value={percentText(format, concentration.top1_share, SHARE_DIGITS)} />
            </div>

            <div>
              <HeroCapInfo
                label="Bottom three"
                tip="The three smallest live retainers together, as a share of all monthly recurring revenue."
              />
              <SubFigure value={percentText(format, concentration.bottom3_share, SHARE_DIGITS)} />
            </div>

            <div>
              <HeroCapInfo
                label="To lose half"
                tip="How many of the largest accounts would have to leave before half of recurring revenue was gone. Smaller is more concentrated — and more fragile."
              />
              <SubFigure value={concentration.clients_to_lose_half} />
            </div>
          </RegionCols>
        </Region>
      </div>

      <Section>
        <SectionHead
          note={`${filterLabel} · ${format.number(rows.length)} listed · ${format.number(
            counts.underScoped,
          )} under-scoped · ${format.number(counts.pastDue)} past due`}
          pushNote
          title="All clients"
        />

        <Card>
          {rows.length === 0 ? (
            <CardBody>
              <Empty>{STATUS_EMPTY[search.status]}</Empty>
            </CardBody>
          ) : (
            <>
              <CardBody scrollX tight>
                <ClientsBook rows={rows} />
              </CardBody>
              <CardMore>
                <More asChild>
                  {/*
                    The mockup's own line names the count — "for the overdue two" —
                    and a tab with none would otherwise offer a page about nobody.
                  */}
                  <Link to="/studio/money">
                    {counts.pastDue === 0
                      ? 'Ageing, retries and the invoice ledger →'
                      : `Ageing, retries and the invoice ledger for the overdue ${format.number(
                          counts.pastDue,
                        )} →`}
                  </Link>
                </More>
              </CardMore>
            </>
          )}
        </Card>

        <Fold summary="How to read allowance burn, % of value and $/h">
          <Prose>
            <B>Allowance burn</B> is delivery against the package allowance for the period, every
            block weighted by its share of the package price; the ochre part is work past it, and it
            is not capped at 100%.
          </Prose>
          <Prose>
            <B>% of value</B> is rate-card value delivered against what the month bills — past ~130%
            for two months running, the block is under-scoped and the row is marked. It is the same
            ratio the under-scoped panel on <Link to="/studio">the overview</Link> draws.
          </Prose>
          <Prose>
            <B>$/h</B> is that same billed figure — this month&rsquo;s retainer line plus the extras
            agreed this month, in USD — over this month&rsquo;s retainer hours. Not what an invoice
            recognised in the month: the run bills extras a month in arrears.
          </Prose>
        </Fold>
      </Section>

      <DecisionsSection
        decisions={decisions}
        filter={search.status}
        filterClients={STATUS_CLIENTS[search.status]}
        rows={rows}
      />
    </>
  )
}
